import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import type { AnalyzedModelsResponse, User } from '@wml-crm/shared';

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function daysSince(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

function daysLabel(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return `${n} день`;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return `${n} дня`;
  return `${n} дней`;
}

export function AnalyzedModelsPage({ me }: { me: User }) {
  const [data, setData] = useState<AnalyzedModelsResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    api<AnalyzedModelsResponse>('/api/analyzed')
      .then(setData)
      .catch((e) => setErr(e instanceof Error ? e.message : 'Ошибка загрузки'));
  }, []);

  async function syncNow() {
    setSyncing(true);
    try {
      setData(await api<AnalyzedModelsResponse>('/api/analyzed/sync', { method: 'POST' }));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Ошибка синка');
    } finally {
      setSyncing(false);
    }
  }

  const sync = data?.sync;

  return (
    <div className="home-inner">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, letterSpacing: '-0.025em' }}>
            Проанализированные модели
          </h1>
          <div style={{ color: 'var(--text-subtle)', fontSize: 13, marginTop: 4 }}>
            Done из To Do · Артем → Модели в работе · {data?.items.length ?? 0} шт
            {sync?.lastSuccessAt && <> · обновлено {fmtDateTime(sync.lastSuccessAt)}</>}
          </div>
        </div>
        {me.isSuperAdmin && (
          <button className="btn-secondary" onClick={syncNow} disabled={syncing}>
            {syncing ? 'Синхронизация…' : 'Обновить из To Do'}
          </button>
        )}
      </div>

      {me.isSuperAdmin && sync && !sync.enabled && (
        <div className="error" style={{ marginBottom: 12 }}>Синк выключен: на сервере не задан TODO_API_KEY.</div>
      )}
      {me.isSuperAdmin && sync?.lastError && (
        <div className="error" style={{ marginBottom: 12 }}>Последний синк упал: {sync.lastError}</div>
      )}
      {err && <div className="error" style={{ marginBottom: 12 }}>{err}</div>}

      {data === null ? (
        !err && <div className="empty-state">Загрузка…</div>
      ) : data.items.length === 0 ? (
        <div className="empty-state">Пока нет моделей со статусом Done</div>
      ) : (
        <div className="card">
          <table className="admin-table">
            <thead>
              <tr><th>Модель в To Do</th><th>Модель в CRM</th><th>Done с</th><th>В Done</th></tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <tr key={it.todoItemId}>
                  <td>{it.name}</td>
                  <td>
                    {it.model ? (
                      <Link to={`/model/${it.model.id}`}>{it.model.displayName}</Link>
                    ) : (
                      <span style={{ color: 'var(--text-subtle)' }} title="Имя в To Do не совпало ни с одной моделью CRM">не найдена</span>
                    )}
                  </td>
                  <td>{it.doneSince ? fmtDate(it.doneSince) : '—'}</td>
                  <td>{it.doneSince ? daysLabel(daysSince(it.doneSince)) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
